/* eslint-disable camelcase,no-useless-computed-key */
import { http } from '@/plugins/http'
import { mixpanel } from '@/plugins/mixpanel'
import { getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import Sigma from '@sigmacloud/sigma-client'
import router from '@/router'
import {
  AUTHENTICATE, AUTHENTICATE_FAILURE,
  REAUTHENTICATION_REQUIRED, REAUTHENTICATE, REAUTHENTICATING, REAUTHENTICATE_FAILURE,
  RECEIVE_INSTANCES,
  REMOVE_INSTANCE,
  SWITCH_ACTIVE_APP_INSTANCE,
  SWITCHING_ACTIVE_APP_INSTANCE,
  LOGOUT,
  VERIFY_AUTH,
  SET_INSTANCE,
  CLEAR_AUTH_STATE,
  CLEAR_STATE,
  CLEAR_LOCALSTORAGE_INSTANCES,
  SET_POST_AUTHORIZATION_ROUTE,
  SET_LOGOUT_NOTIF_MSG,
  SABOTAGE_TOKEN,
  SET_AUTH_VERIFICATION_TIMER,
  RENEW_TOKEN,
  RENEW_TOKEN_FAILURE
} from '../mutationTypes'
import authService from '../../utils/auth'
import { checkForMigratedInstance, doesSiteExist } from '../../utils/helpers/SigmacloudRetiring'
import { startAuthVerifier, stopAuthVerifier } from '@/plugins/authverification/auth_verifier'

const state = {
  user: {
    username: null,
    email: null,
    userId: null,
    isLoggedIn: false,
    isSuspended: null,
    permissions: []
  },
  instanceUrl: null,
  instanceServer: '.pa.revolutiones.com/',
  authenticating: false,
  failure: null,
  reAuthRequired: false,
  reauthenticating: false,
  reAuthFailure: null,
  instances: [],
  switchingActiveAppInstance: false,
  postAuthorizationRoute: '',
  logoutNotifMsg: '',
  authVerificationTimer: null,
  renewalRequested: false,
  lastActiveInstance: null,
  jasperhost: null,
  sigmaqueryurl: null
}

/**
 * Helper function to return a reference to the active instance
 * @return {*}
 */
const activeInstance = () => {
  return state.instances.find(instance => instance.url === sessionStorage.getItem('instanceUrl')) || {}
}

const actions = {
  /**
   * Authenticates the user
   * @param store
   * @param payload
   * @return {*}
   */
  async forgotPassword (store, payload) {
    return await authService.forgotPassword(payload)
  },
  async saveNewPassword (store, payload) {
    return await authService.saveNewPassword(payload)
  },
  async changePassword (store, payload) {
    const headers = getHeaders(this.getters.getToken)
    return await authService.changePassword(payload, headers)
  },
  async authenticate (store, payload) {
    const isReauth = payload && payload.reauth === true

    if (isReauth) {
      this.commit(REAUTHENTICATE_FAILURE, null)
      this.commit(REAUTHENTICATING, true)
    } else {
      this.commit(AUTHENTICATE_FAILURE, null)
    }

    if (!payload) {
      if (isReauth) {
        this.commit(REAUTHENTICATING, false)
        this.commit(REAUTHENTICATE_FAILURE, 'Missing payload')
      } else {
        this.commit(AUTHENTICATE_FAILURE, 'Missing payload')
      }
      return
    }

    try {
      const newURL = await checkForMigratedInstance(payload, state)
      if (newURL) {
        payload.url = newURL
        await this.commit(SWITCH_ACTIVE_APP_INSTANCE, payload)
        await this.commit('UPDATE_LAST_ACTIVE_INSTANCE', payload)
      }

      let response
      if (isReauth) {
        response = await authService.reAuthenticate(payload)
        this.commit(REAUTHENTICATE, response)
        this.commit(REAUTHENTICATING, false)
      } else {
        response = await authService.authenticate(payload)
        this.commit(SWITCHING_ACTIVE_APP_INSTANCE, false)
        this.commit(AUTHENTICATE, { token: response.token, url: payload.url })
        await Promise.all([
          store.dispatch('fetchUiSetting', { name: 'AR_ENABLED' }),
          store.dispatch('fetchUiSetting', { name: 'PAYROLL_ENABLED' }),
          store.dispatch('fetchUiSetting', { name: 'COST_REPORTER_BETA_ENABLED' }),
          store.dispatch('fetchUiSetting', { name: 'ACH_ENABLED' }),
          store.dispatch('fetchUiSetting', { name: 'TRIAL_BALANCE_REPORT_DEFAULT_SETTINGS' }),
          store.dispatch('fetchUiSetting', { name: 'GL_REPORT_DEFAULT_SETTINGS' }),
          store.dispatch('fetchUiSetting', { name: 'TRANSACTION_ENTRY_OPTIONS' }),
          store.dispatch('fetchHomePageTextUiSetting'),
          store.dispatch('fetchDocSequenceSettings')
        ])
      }

      return response
    } catch (e) {
      this.commit(SWITCHING_ACTIVE_APP_INSTANCE, false)

      if (isReauth) {
        console.log(e)
        this.commit(REAUTHENTICATING, false)
        this.commit(REAUTHENTICATE_FAILURE, e)
        return e
      }

      let msg
      if (e && e.status) {
        switch (parseInt(e.status)) {
          case 400:
            if (e.body && e.body.non_field_errors) {
              msg = e.body.non_field_errors[0]
            } else {
              msg = 'Unable to login'
            }
            break
          case 403:
            if (e.body && (e.body.locked || e.body.password_expired)) {
              msg = e.body.message
            } else {
              msg = 'Unable to login'
            }
            break
          default:
            if (e.statusText) {
              msg = e.statusText
            } else {
              msg = 'Error authenticating'
            }
        }
      } else if (e && !e.status) {
        if (!e.bodyText) {
          const instance = this.getters.getActiveInstance
          if (instance) this.dispatch('removeInstance', instance.url)
          msg = 'No response received. Please specify a valid SigmaCloud instance.'
        } else {
          msg = 'Error authenticating'
        }
      } else {
        msg = null
      }
      this.commit(AUTHENTICATE_FAILURE, msg)
      if (e && e.body && e.body.password_expired) {
        return router.push(`setup?token=${e.body.token}&instances=${payload.url}&username=${payload.username}`)
      }
      return Promise.reject(e)
    }
  },
  async authenticateWithToken (store, payload) {
    this.commit(AUTHENTICATE_FAILURE, null)

    if (!(payload && payload.token && payload.url)) {
      this.commit(AUTHENTICATE_FAILURE, 'Missing token payload')
      return Promise.reject(new Error('Missing token payload'))
    }

    this.commit(AUTHENTICATE, payload)

    if (!this.getters.getToken) {
      this.commit(AUTHENTICATE_FAILURE, 'Unable to login with token')
      return Promise.reject(new Error('Unable to login with token'))
    }

    await Promise.all([
      store.dispatch('fetchUiSetting', { name: 'AR_ENABLED' }),
      store.dispatch('fetchUiSetting', { name: 'PAYROLL_ENABLED' }),
      store.dispatch('fetchUiSetting', { name: 'COST_REPORTER_BETA_ENABLED' }),
      store.dispatch('fetchUiSetting', { name: 'ACH_ENABLED' }),
      store.dispatch('fetchUiSetting', { name: 'TRIAL_BALANCE_REPORT_DEFAULT_SETTINGS' }),
      store.dispatch('fetchUiSetting', { name: 'GL_REPORT_DEFAULT_SETTINGS' }),
      store.dispatch('fetchUiSetting', { name: 'TRANSACTION_ENTRY_OPTIONS' }),
      store.dispatch('fetchHomePageTextUiSetting'),
      store.dispatch('fetchDocSequenceSettings')
    ])

    return payload
  },
  /**
   * Kept for backwards compatibility — delegates to `authenticate` with reauth flag.
   */
  async reauthenticate (store, payload) {
    payload = payload || {}
    payload.reauth = true
    return this.dispatch('authenticate', payload)
  },
  /**
   * Requests a new token from the API
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async requestNewToken (store, payload) {
    const activeInstance = store.getters.getActiveInstance
    try {
      const response = await authService.renew({ url: activeInstance.url, token: activeInstance.token })
      if (response && response.status === 200 && response.body) {
        this.commit(RENEW_TOKEN, response.body)
      } else {
        throw new Error(response)
      }
    } catch (e) {
      console.log(e)
      this.commit(RENEW_TOKEN_FAILURE)
      return e
    }
  },
  /**
   * Verify auth status (useful if page reloaded or state is reset)
   * @param store
   * @return {void|*}
   */
  verifyAuth (store) {
    this.commit(VERIFY_AUTH)
  },
  /**
   * Triggers the logout process
   * @param store
   * @return {void|*}
   */
  logout (store) {
    // Clear the mixpanel identity so a different user on the same browser doesn't inherit the
    // prior distinct_id (mixpanel-browser persists it; the old server SDK was stateless). CP2.1.
    if (mixpanel && typeof mixpanel.reset === 'function') {
      mixpanel.reset()
    }
    this.commit(LOGOUT)
    this.commit(CLEAR_AUTH_STATE)
    this.commit(CLEAR_STATE)
  },
  /**
   * Gets available instances
   * @param store
   * @param payload
   * @return {*|PromiseObj|PromiseObj|Promise<T | never>|Promise|PromiseObj}
   */
  async fetchInstances (store, payload) {
    try {
      const resp = await http.get('/instances.json')
      if (!(resp && resp.hasOwnProperty('body'))) {
        throw new Error('No instances returned from GET /instances.json')
      }
      let instances = resp.body
      if (payload && instances.length) {
        payload = payload.split(',')
          .filter(v => !instances.find(x => x.url === v)) // remove duplicates if json response contains same urls as instances passed via query string
          .map(v => ({ name: '', url: v }))               // put instance urls passed via query string in proper format
        instances = [...instances, ...payload]
      }
      if (payload) {
        payload = payload.split(',')
        payload.forEach(async (instance) => {
          let field
          let exp = new RegExp(`^(https:\\/\\/)?([a-zA-Z0-9_-]+)(.pa.revolutiones.com/)?\\/?$`, 'i')
          if (instance.includes('127.0.0') || instance.includes('localhost') || instance.includes('https') || instance.includes('http')) {
            field = instance
            if (!await doesSiteExist(field)) return
          } else {
            field = instance.match('.pa.revolutiones.com/') ? instance : (instance.match('https://') ? '' : 'https://') + instance.replace(exp, '$2') + '.pa.revolutiones.com/'
            if (field[field.length - 1] !== '/') field = field + '/'
            if (!await doesSiteExist(field)) {
              field = 'https://' + instance.replace(exp, '$2') + '.pa.probooks.revolutiones.org/'
              if (field[field.length - 1] !== '/') field = field + '/'
              if (!await doesSiteExist(field)) return
            }
          }
          await store.dispatch('addInstance', field, instance.replace(exp, '$2'))
        })
      }
      this.commit(RECEIVE_INSTANCES, instances)
      return resp
    } catch (e) {
      console.log(e)
      if (payload) {
        const instances = payload.split(',').map(v => ({ name: '', url: v }))
        this.commit(RECEIVE_INSTANCES, instances)
      }
      return e
    }
  },
  /**
   * Fetches the current user's permissions
   * @param store
   * @return {Promise<void>}
   */
  async fetchUserPermissions (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    let resp
    try {
      resp = await http.get(buildUrlWithQuery(`auth/permissions`, null, instanceUrl), { headers })
        .then(parseJSON)
      return resp
    } catch (e) {
      if (e instanceof Error && !(e.name === 'SyntaxError' && e.message === 'Unexpected end of JSON input')) {
        console.log(e)
      }
      return {}
    } finally {
      if (resp && resp.permissions) {
        this.commit('SET_USER_PERMISSIONS', resp.permissions && Array.isArray(resp.permissions) ? resp.permissions : [])
      }
    }
  },
  /**
   * Fetches the current user's data by id
   * @param store
   * @return {Promise<void>}
   */
  fetchUserById (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return http.get(buildUrl(`users/${id}`, null, instanceUrl), { headers })
      .then(parseJSON)
      .then(response => {
        return response
      })
      .catch(e => {
        return e
      })
  },
  /**
   * Adds an instance
   * @param store
   * @param payload
   * @return {void|*}
   */
  addInstance (store, url, name) {
    if (url) {
      let instance = { url: url, name: name }
      if (instance.url[instance.url.length - 1] !== '/') {
        instance.url = instance.url + '/'  // in case trailing '/' is missing
      }
      this.commit(RECEIVE_INSTANCES, [instance])
      return instance.url
    }
  },
  /**
   * Removes an instance
   * @param store
   * @param payload
   * @return {void|*}
   */
  removeInstance (store, payload) {
    if (payload) {
      return this.commit(REMOVE_INSTANCE, payload)
    }
  },
  /**
   * Switches the currently active instance
   * @param store
   * @param payload
   * @return {void|*}
   */
  switchActiveAppInstance (store, payload) {
    if (payload) {
      this.commit(SWITCHING_ACTIVE_APP_INSTANCE, true)
      this.commit(SWITCH_ACTIVE_APP_INSTANCE, payload)
    } else {
      this.commit(SWITCHING_ACTIVE_APP_INSTANCE, false)
    }
  },
  /**
   * Resets the authentication failure flag to null
   * @param store
   */
  resetAuthFailure (store) {
    this.commit(AUTHENTICATE_FAILURE, null)
  },
  /**
   * Resets the re-authentication failure flag to null
   * @param store
   */
  resetReauthFailure (store) {
    this.commit(REAUTHENTICATE_FAILURE)
  },
  /**
   * Clears the auth state, except for the list of instances
   * @param store
   */
  clearAuthState (store) {
    this.commit(CLEAR_AUTH_STATE)
  },
  /**
   * Clears all instances set in localStorage
   * @param store
   */
  clearLocalStorageInstances (store) {
    this.commit(CLEAR_LOCALSTORAGE_INSTANCES)
  },
  /**
   * Checks the status of the last request. If the response is 401 (Unauthorized), determines that reauthentication
   * is probably required.
   * @param store
   * @param response
   */
  checkLastRequestStatus (store, response) {
    if (response.hasOwnProperty('status')) {
      const { status } = response
      switch (status) {
        case 401:
          this.commit(REAUTHENTICATION_REQUIRED, true)
          break
        default:
          this.commit(REAUTHENTICATION_REQUIRED, false)
      }
    }
  },
  /**
   * Payments the status of the last request. If the response is 401 (Unauthorized), determines that reauthentication
   * is probably required.
   * @param store
   * @param response
   */
  paymentLastRequestStatus (store, response) {
    if (response.hasOwnProperty('status')) {
      const { status } = response
      switch (status) {
        case 401:
          this.commit(REAUTHENTICATION_REQUIRED, true)
          break
        default:
          this.commit(REAUTHENTICATION_REQUIRED, false)
      }
    }
  },
  /**
   * Checks whether the token has expired
   * @param store
   * @param payload
   * @return {getters.getActiveInstance.exp|getActiveInstance.exp}
   */
  checkTokenExpiration (store, payload) {
    const active = this.getters.getActiveInstance
    let { exp, token } = active
    if (!token) {
      this.commit(REAUTHENTICATION_REQUIRED, true)
      return false
    } else if (Math.round(Date.now() / 1000) >= exp) {
      this.commit(REAUTHENTICATION_REQUIRED, true)
      return false
    } else {
      this.commit(REAUTHENTICATION_REQUIRED, false)
      return true
    }
  },
  /**
   * Fetches instance settings
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async fetchUiSetting (store, payload) {
    const { name, query } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      let resp = await http.get(buildUrlWithQuery(`settings${'/' + name}`, query || null, instanceUrl), { headers })
        .then(parseJSON)
      if (resp) {
        this.commit('SET_UI_SETTING', resp)
      }
      return resp
    } catch (e) {
      if (e instanceof Error && !(e.name === 'SyntaxError' && e.message === 'Unexpected end of JSON input')) {
        console.log(e)
      }
      this.commit('SET_UI_SETTING', { name, value: null })
      return Promise.resolve()
    }
  },
  /**
   * Fetches Home Page Text settings
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async fetchHomePageTextUiSetting (store) {
    let pageData = {name: 'HOME_PAGE', value: ''}
    const instanceUrl = 'https://sandbox.staging.pa.probooks.revolutiones.org/'
    try {
      let resp = await http.get(buildUrlWithQuery(`home_page_content`, {} || null, instanceUrl), {})
        .then(parseJSON)
      if (resp) {
        pageData.value = resp.data
        this.commit('SET_UI_SETTING', pageData)
      }
      return pageData
    } catch (e) {
      if (e instanceof Error && !(e.name === 'SyntaxError' && e.message === 'Unexpected end of JSON input')) {
        console.log(e)
      }
      this.commit('SET_UI_SETTING', { name, value: null })
      return Promise.resolve()
    }
  },
  /**
   * Fetches Number sequence setting
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async fetchDocSequenceSettings (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      let resp = await http.get(buildUrlWithQuery(`doc-sequence-settings`, null, instanceUrl), { headers })
        .then(parseJSON)
      if (resp && resp.length) {
        const jeSequenceSettings = resp.find(setting => setting.module === 'JE')
        if (jeSequenceSettings && jeSequenceSettings.settings && jeSequenceSettings.settings.length) {
          jeSequenceSettings.settings.forEach(jeSequenceSetting => {
            if (jeSequenceSetting.field === 'number') {
              this.commit('SET_UI_SETTING', { name: 'JE_NUMBER_SEQUENCE', value: jeSequenceSetting.sequence })
            }
          })
        }
      } else {
        this.commit('SET_UI_SETTING', { name: 'JE_NUMBER_SEQUENCE', value: 'SYSTEM_ASSIGNED' })
      }
      return resp
    } catch (e) {
      if (e instanceof Error && !(e.name === 'SyntaxError' && e.message === 'Unexpected end of JSON input')) {
        console.log(e)
      }
      this.commit('SET_UI_SETTING', { name: 'JE_NUMBER_SEQUENCE', value: 'SYSTEM_ASSIGNED' })
      return Promise.resolve()
    }
  },
  /**
   * Sets a specific route to which to navigate after logging in
   * @param store
   * @param payload
   */
  setPostAuthorizationRoute (store, payload) {
    this.commit(SET_POST_AUTHORIZATION_ROUTE, payload)
    return payload
  },
  /**
   * Sets the notification message to display when logged out
   * @param store
   * @param payload
   */
  setLogoutNotifMsg (store, payload) {
    this.commit(SET_LOGOUT_NOTIF_MSG, payload)
  },
  /**
   * Assigns the auth verification timer id to a state property, so that it can be referred to and
   * cleared using clearInterval()
   * @param store
   * @param payload
   */
  setAuthVerificationTimer (store, payload) {
    this.commit(SET_AUTH_VERIFICATION_TIMER, payload)
  },
  setReauthRequired (store) {
    this.commit(REAUTHENTICATION_REQUIRED, true)
    return true
  },
  sabotageToken (store) {
    this.commit(SABOTAGE_TOKEN)
  },
  /**
   * Updates the localStorage.lastActiveInstance property
   * @param store
   * @param payload
   */
  updateLastActiveInstance (store, payload) {
    this.commit('UPDATE_LAST_ACTIVE_INSTANCE', payload)
  },
  /**
   * Fetches the jasper url
   * @param store
   * @return {Promise<void>}
   */
  async fetchJasperHost (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    let resp
    try {
      resp = await http.get(buildUrlWithQuery(`jasper-host`, null, instanceUrl), { headers })
        .then(parseJSON)
      return resp
    } catch (e) {
      if (e instanceof Error && !(e.name === 'SyntaxError' && e.message === 'Unexpected end of JSON input')) {
        console.log(e)
      }
      return {}
    } finally {
      if (resp && resp.jasper_hostname) {
        this.commit('SET_JASPER_HOST', resp && resp.jasper_hostname ? resp.jasper_hostname : null)
      }
    }
  },
  /**
   * Gets available urls
   * @param store
   * @param payload
   * @return {*|PromiseObj|PromiseObj|Promise<T | never>|Promise|PromiseObj}
   */
  async fetchSigmaQueryUrl (store) {
    try {
      const resp = await http.get('/sigmaquery.json')
      let url = resp.body
      this.commit('SET_SIGMA_QUERY_URL', url)
    } catch (e) {
      console.log(e)
      return e
    }
  }
}

const getters = {
  getToken: state => {
    if (!(state.instances && state.instances.length)) {
      return ''
    }
    if (activeInstance().url === state.instanceUrl) {
      return activeInstance().token
    }
    return ''
  },
  getUser: state => state.user || {},
  getPostAuthorizationRoute: state => state.postAuthorizationRoute || '',
  getLogoutNotifMsg: state => state.logoutNotifMsg || '',
  isLoggedIn: state => state.user.isLoggedIn,
  isReauthenticating: state => state.reauthenticating,
  getAuthFailure: state => state.failure,
  getReauthStatus: state => state.reAuthRequired,
  getReauthFailure: state => state.reAuthFailure,
  getInstanceServer: state => state.instanceServer ? state.instanceServer : '',
  getInstances: state => {
    let exp = new RegExp(`^(https:\\/\\/)?([a-zA-Z0-9_-]+)(?:\\.pa\\.probooks\\.revolutiones\\.org|\\.pa\\.revolutiones\\.com)?\\/?$`, 'i')
    return state.instances ? state.instances.map(instance => {
      try {
        const instanceName = instance.url ? instance.url.match(exp) : null
        if (instanceName && instanceName[2]) {
          instance.name = instanceName[2]
        } else if (instance.url) {
          instance.name = instance.url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').split('.')[0]
        } else {
          instance.name = ''
        }
        return instance
      } catch (e) {
        return {}
      }
    }) : []
  },
  getActiveInstance: state => {
    if (state.instances && state.instances.length && state.instanceUrl === activeInstance().url) {
      let active = activeInstance()
      try {
        return {...active, name: active.url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').split('.')[0]}
      } catch (e) {
        return activeInstance()
      }
    }
    return {}
  },
  getAvailableInstances: state => {
    let exp = new RegExp(`^(https:\\/\\/)?([a-zA-Z0-9_-]+)(?:\\.pa\\.probooks\\.revolutiones\\.org|\\.pa\\.revolutiones\\.com)?\\/?$`, 'i')
    return state.instances && state.instances.length ? state.instances.filter(instance => instance.url !== activeInstance().url).map(instance => {
      try {
        const instanceName = instance.url ? instance.url.match(exp) : null
        if (instanceName && instanceName[2]) {
          instance.name = instanceName[2]
        } else if (instance.url) {
          instance.name = instance.url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').split('.')[0]
        } else {
          instance.name = ''
        }
        return instance
      } catch (e) {
        return {}
      }
    }) : []
  },
  getSwitchingActiveAppInstanceStatus: state => state.switchingActiveAppInstance,
  getAuthVerificationTimer: state => state.authVerificationTimer,
  renewalRequested: state => state.renewalRequested,
  getUiSettings: state => {
    return activeInstance().settings ? activeInstance().settings : null
  },
  /**
   * Checks whether the user has the specified permission parameters
   * @return {boolean}
   */
  checkIfUserHasRequiredPermissions: (state) => (requiredPermissions) => {
    return state.user.permissions && requiredPermissions.every(permission => state.user.permissions.includes(permission))
  },
  checkIfUserHasAnyOfThePermissions: (state) => (requiredPermissions) => {
    return state.user.permissions && requiredPermissions.some(permission => state.user.permissions.includes(permission))
  },
  getJasperHost: state => {
    return state.jasperhost ? state.jasperhost : null
  },
  getSigmaQueryUrl: state => {
    return state.sigmaqueryurl ? state.sigmaqueryurl : null
  }
}

const mutations = {
  [SET_INSTANCE] (state, payload) {
    state.instanceServer = payload
  },
  /**
   * Sets JWT data in state after authentication.
   * @param state
   * @param payload
   */
  [AUTHENTICATE] (state, payload) {
    setJwtData(state, payload)
  },
  /**
   * Sets authentication error to state.failure
   * @param state
   * @param e
   */
  [AUTHENTICATE_FAILURE] (state, e) {
    state.failure = e
  },
  /**
   * Sets state property that indicates whether reauthentication is required
   * @param state
   * @param payload
   */
  [REAUTHENTICATION_REQUIRED] (state, payload) {
    state.reAuthRequired = payload
  },
  /**
   * Sets a new token in state instances array and syncs that array with localStorage.instances whenever reauthentication
   * has occurred, then sets state.reAuthRequired and state.renewalRequested back to false.
   * @param state
   * @param payload
   */
  [REAUTHENTICATE] (state, payload) {
    stopAuthVerifier()
    state.instances = JSON.parse(localStorage.getItem('instances'))
    const { token } = payload.data
    if (!token) {
      state.failure = 'No token received'
      state.reAuthRequired = true
      return
    }
    const decoded = authService.getJwtPayload(token)
    if (!decoded) {
      state.failure = 'No token received'
      state.reAuthRequired = true
      return
    }
    if (!(decoded.username === state.user.username)) {
      state.failure = 'Credential mismatch'
      state.reAuthRequired = true
      return
    }
    const targetInstance = state.instances.findIndex(v => (v.url === payload.url || v.url === `${payload.url}/`))
    if (targetInstance > -1) {
      state.instances[targetInstance]['token'] = token
      state.instances[targetInstance]['exp'] = decoded.exp
    }
    state.reAuthRequired = false
    state.renewalRequested = false
    localStorage.setItem('instances', JSON.stringify(state.instances))
    startAuthVerifier()
  },
  /**
   * Sets a 'reauthenticating' property in state that acts as a flag that indicates the user is reauthenticating
   * @param state
   * @param payload
   */
  [REAUTHENTICATING] (state, payload) {
    state.reauthenticating = payload
  },
  /**
   * Handles authentication failure.
   * @param state
   * @param e
   */
  [REAUTHENTICATE_FAILURE] (state, e) {
    if (e && e.hasOwnProperty('status')) {
      switch (parseInt(e.status)) {
        case 400:
          if (e.body && e.body.non_field_errors) {
            state.reAuthFailure = e.body.non_field_errors[0]
          } else {
            state.reAuthFailure = 'Unable to login'
          }
          break
        default:
          if (e.statusText) {
            state.reAuthFailure = e.statusText
          } else {
            state.reAuthFailure = 'Error reauthenticating'
          }
      }
    } else {
      state.reAuthFailure = ''
    }
  },
  /**
   * Receives instances and sets them in state.instances.
   * @param state
   * @param payload
   */
  [RECEIVE_INSTANCES] (state, payload) {
    if (!(payload && Array.isArray(payload) && payload.length)) {
      return
    }
    state.instances = JSON.parse(localStorage.getItem('instances')) || [] // Get all instances found in localstorage and set them in state.
    payload.forEach(instance => {
      let exists
      if (state.instances && Array.isArray(state.instances) && state.instances.length) {
        exists = state.instances.find(v => v.url.includes(instance.url))
      }
      if (!exists) {
        instance['token'] = null
        instance['exp'] = null
        if (instance.url[instance.url.length - 1] !== '/') {
          instance.url = instance.url + '/'  // in case trailing '/' is missing
        }
        state.instances.push(instance)
      }
    })
    localStorage.setItem('instances', JSON.stringify(state.instances))
  },
  /**
   * Removes an instance from state.instances. Once done, checks whether the active instance was the instance that was just
   * removed. If so, attempts to set state.lastActiveInstance to the next available instance. If no instances are available,
   * sets state.lastActiveInstance to null. Then, sets sessionStorage's "instanceUrl" property to the value of
   * state.lastActiveInstance. If state.lastActiveInstance is null, removes the "instanceUrl" key from sessionStorage.
   * Lastly, updates localStorage's "instances" property with the value of state.instances.
   * @param state
   * @param payload
   */
  [REMOVE_INSTANCE] (state, payload) {
    state.instances = state.instances.filter(instance => instance.url !== (payload || `${payload}/`))
    if (!activeInstance().url) {
      state.lastActiveInstance = state.instances.length ? state.instances[0].url : null
      localStorage.setItem('lastActiveInstance', state.lastActiveInstance)
    }
    if (state.lastActiveInstance) {
      sessionStorage.setItem('instanceUrl', state.lastActiveInstance)
      state.instanceUrl = state.lastActiveInstance
    } else {
      sessionStorage.setItem('instanceUrl', null)
      state.instanceUrl = null
    }
    localStorage.setItem('instances', JSON.stringify(state.instances))
  },
  /**
   * Handles logout by invalidating the token for the currently active instance and removing any "settings" property
   * that that instance object might have. The sets state.instances to a copy of itself in order to trigger a state change.
   * Since JS objects are "pass by reference", it is safe to modify and remove properties directly on the return value of
   * activeInstance().
   * @param state
   */
  [LOGOUT] (state) {
    activeInstance().token = ''
    activeInstance().exp = ''
    if (activeInstance().hasOwnProperty('settings')) {
      delete activeInstance().settings
    }
    localStorage.setItem('instances', JSON.stringify(state.instances))
    stopAuthVerifier()
    state.renewalRequested = false
  },
  /**
   * The purpose of this mutation is to set the auth state as well as the tab's sessionStorage.instanceUrl property
   * when the app is first loaded or when the page is refreshed. It is necessary to set the "instances" and
   * "lastActiveInstance" properties in state as well as in localStorage because changes to localStorage are not picked
   * up by Vue's reactivity system, which prevents getters from updating and in turn can prevent certain watchers and
   * computed properties from working properly.
   * @param state
   */
  [VERIFY_AUTH] (state) {
    stopAuthVerifier()
    const instances = JSON.parse(localStorage.getItem('instances')) // get instances from localstorage
    if (!(instances && instances.length)) { // return if none exist
      state.user.isLoggedIn = false
      return
    }
    state.instances = instances // set instances in state
    state.lastActiveInstance = localStorage.getItem('lastActiveInstance') // set last active instance from localstorage
    if (state.lastActiveInstance && !sessionStorage.getItem('instanceUrl')) { // If sessionStorage doesn't contain a reference to any instance
      sessionStorage.setItem('instanceUrl', state.lastActiveInstance) // set the tab's instanceUrl based on the last active instance
      state.instanceUrl = state.lastActiveInstance                    // and sync that value with state
    } else if (sessionStorage.getItem('instanceUrl')) { // otherwise
      state.instanceUrl = sessionStorage.getItem('instanceUrl') // set state with the instanceUrl from sessionStorage
    }
    if (activeInstance()) { // retrieve the active instance
      const { token, url } = activeInstance()  // get the token from the active instance
      // Set Instance for Sigma Client Resources
      Sigma.setInstance(url, token)
      if (token && token.length > 0) {    // set auth data in state from token
        const decoded = authService.getJwtPayload(token)
        let { user_id, username, email } = decoded
        state.user.isLoggedIn = true
        state.user.username = username
        state.user.email = email
        state.user.userId = user_id
        state.renewalRequested = false
        if (window.DD_RUM) {
          const instanceName = url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').split('.')[0]
          window.DD_RUM.setUser({ id: user_id, name: username, email: email, instance: instanceName })
        }
        startAuthVerifier()
      } else {
        state.user.isLoggedIn = false
        state.user.username = ''
        state.user.email = ''
        state.user.userId = ''
      }
    } else {
      state.user.isLoggedIn = false
    }
  },
  /**
   * Changes the instance in the tab's sessionStorage and sets the last active instance used in localStorage
   * @param state
   * @param payload
   */
  [SWITCH_ACTIVE_APP_INSTANCE] (state, payload) {
    sessionStorage.setItem('instanceUrl', payload.url)
    state.instanceUrl = payload.url
    localStorage.setItem('lastActiveInstance', payload.url)
    state.lastActiveInstance = payload.url
  },
  /**
   * Updates the state property and localStorage key that indicate which instance was last used
   * @param state
   * @param payload
   * @constructor
   */
  ['UPDATE_LAST_ACTIVE_INSTANCE'] (state, payload) {
    localStorage.setItem('lastActiveInstance', payload.url)
    state.lastActiveInstance = payload.url
  },
  /**
   * Flag used to determine when we are switching the active app instance
   * @param state
   * @param payload
   */
  [SWITCHING_ACTIVE_APP_INSTANCE] (state, payload) {
    state.switchingActiveAppInstance = payload
  },
  /**
   * Sets a 'settings' property for the current instance
   * @param state
   * @param payload
   * @constructor
   */
  ['SET_UI_SETTING'] (state, payload) {
    state.instances = JSON.parse(localStorage.getItem('instances'))
    if (!(activeInstance() && activeInstance().settings && typeof activeInstance().settings === 'object')) {
      activeInstance().settings = {}
    }
    activeInstance().settings[payload.name] = payload.value
    localStorage.setItem('instances', JSON.stringify(state.instances))
  },
  /**
   * Clears the auth state
   * @param state
   */
  [CLEAR_AUTH_STATE] (state) {
    state.user = {
      username: '',
      email: '',
      userId: '',
      isLoggedIn: false,
      isSuspended: false
    }
    if (window.DD_RUM) {
      window.DD_RUM.clearUser()
    }
    state.failure = null
    state.authenticating = false
    state.reAuthRequired = false
    state.switchingActiveAppInstance = false
    state.setAuthVerificationTimer = null
    state.renewalRequested = false
  },
  /**
   * Remove instances from localstorage
   * @param state
   */
  [CLEAR_LOCALSTORAGE_INSTANCES] (state) {
    localStorage.removeItem('instances')
  },
  /**
   * Sets a route path that should be used after authentication. This is useful because if the user's token expires and
   * they are returned to the login view, then after logging back in, this enables them to return to the route they
   * were on.
   * @param state
   * @param payload
   */
  [SET_POST_AUTHORIZATION_ROUTE] (state, payload) {
    state.postAuthorizationRoute = payload
  },
  /**
   * Sets a notification to be displayed when the user is returned to the login view.
   * @param state
   * @param payload
   */
  [SET_LOGOUT_NOTIF_MSG] (state, payload) {
    state.logoutNotifMsg = payload
  },
  /**
   * Assigns the timer from the setInterval method that is called in the 'auth_verifier.js' plugin to the
   * 'authVerificationTimer' state property.
   * @param state
   * @param payload
   */
  [SET_AUTH_VERIFICATION_TIMER] (state, payload) {
    state.authVerificationTimer = payload
  },
  /**
   * Mutation that is called when the token should be renewed.
   * TODO: you're going to have to change this one
   * @param state
   * @param payload
   */
  [RENEW_TOKEN] (state, payload) {
    payload.url = activeInstance().url
    setJwtData(state, payload)
  },
  /**
   * Mutation that is called when the token fails to renew.
   * @param state
   */
  [RENEW_TOKEN_FAILURE] (state) {
    stopAuthVerifier()
    state.renewalRequested = true
    state.reAuthRequired = true
  },
  /**
   * Mutation that sets the current user's permissions in state
   * @param state
   * @param payload
   * @constructor
   */
  ['SET_USER_PERMISSIONS'] (state, payload) {
    if (payload && Array.isArray(payload)) {
      state.user.permissions = payload
    } else {
      state.user.permissions = []
    }
  },

   /**
   * Mutation that sets the current user's permissions in state
   * @param state
   * @param payload
   * @constructor
   */
  ['SET_JASPER_HOST'] (state, payload) {
    if (payload) {
      state.jasperhost = payload
    } else {
      state.jasperhost = null
    }
  },
  /**
   * Mutation that sets the current user's permissions in state
   * @param state
   * @param payload
   * @constructor
   */
  ['SET_SIGMA_QUERY_URL'] (state, payload) {
    if (payload) {
      state.sigmaqueryurl = payload
    } else {
      state.sigmaqueryurl = null
    }
  },
  /**
   * Test mutation that kills the token
   * @param state
   */
  [SABOTAGE_TOKEN] (state) {
    state.instances = JSON.parse(localStorage.getItem('instances'))
    if (activeInstance().exp) {
      activeInstance().exp = (new Date().getTime() / 1000) + 3
    }
    console.log('Token expiration timestamp changed to 3s from now')
    localStorage.setItem('instances', JSON.stringify(state.instances))
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}

function setJwtData (state, { token, url }) {
  stopAuthVerifier()
  try {
    if (!token) {
      throw new Error('No token received')
    }
    const decoded = authService.getJwtPayload(token)
    if (!decoded) {
      throw new Error('Error decoding token')
    }
    state.renewalRequested = false
    const { exp, username, email, user_id } = decoded
    state.instances = JSON.parse(localStorage.getItem('instances'))
    state.user.username = username
    state.user.email = email
    state.user.userId = user_id
    state.user.isLoggedIn = true
    if (window.DD_RUM) {
      const instanceName = url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').split('.')[0]
      window.DD_RUM.setUser({ id: user_id, name: username, email: email, instance: instanceName })
    }
    const targetInstance = state.instances.find(instance => instance.url === url)
    targetInstance.token = token
    if (exp) {
      targetInstance.exp = exp
    }
    localStorage.setItem('instances', JSON.stringify(state.instances))
    if (targetInstance.url === activeInstance().url && activeInstance().exp) {
      startAuthVerifier()
    }
  } catch (e) {
    console.log(e)
    stopAuthVerifier()
  }
}
